import type { DataTableColumn } from 'mantine-datatable';
import { useCallback, useMemo } from 'react';

import { useCurrencyQueryActions } from '@/api/hooks/currencyQueryActions';
import { useEstimationCostQueryActions } from '@/api/hooks/estimationCostQueryActions';
import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import type {
  CurrencyResponse,
  EstimationAdditionalFieldResponse,
  EstimationCostResponse,
} from '@/api/schema/data-contracts';
import { ApiEstimationItemPurpose, ApiEstimationItemStatus } from '@/api/schema/data-contracts';
import { CurrencyCode } from '@/components/atoms/CurrencyCode';
import { EstimationUnitName } from '@/components/atoms/EstimationUnitName';
import { Stack } from '@/components/atoms/Stack';
import TableSkeleton from '@/components/atoms/TableSkeleton';
import Search from '@/components/molecules/Search';
import { TableColumnsSelector } from '@/components/organism/DataTable/components/columnsSelector';
import { ExportButtonsSelector } from '@/components/organism/DataTable/components/exportSelector';
import { TablePhasesSelector } from '@/components/organism/DataTable/components/phasesSelector';
import { eExportFormat } from '@/components/organism/DataTable/model';
import type { EstimationExportButtonsProps } from '@/components/organism/EstimationExportButtons';
import { ProjectInfoMessages } from '@/components/organism/NewEstimation/ProjectBasics/ProjectInfo/messages';
import { messages as sidebarMessageDefinitions } from '@/components/organism/Sidebar/messages';
import { useMessages } from '@/hooks/messages';
import { useEstimationExport } from '@/hooks/useEstimationExport';
import { Route } from '@/routes/estimation/detail/$estimationId/$tabId';
import type { OmitSafely } from '@/types';
import { getPhaseAndDescendantIds } from '@/utils/phasesFiltering';
import { sortCostTypes, sortExpressions, sortResourceTypes } from '@/utils/sorting';

import type { CostEstimationItemsGroupedTableProps } from '../../CostEstimationItemsGroupedTables';
import EstimationItemDetailModal from '../../EstimationItemDetailModal';
import type { EstimationItemsGroupedTableProps } from '../../EstimationItemsGroupedTables';
import { Layout } from '../../Layout';
import {
  getCostEstimationItemDetail,
  getResourceEstimationItemDetail,
} from '../../mappers/estimationItemDetailMapper';
import { estimationTabMessageDefinitions } from '../../messages';
import type {
  CostEstimationItemsTableRecord,
  CostModel,
  EstimationItemActionCallback,
  ResourceEstimationItemsTableRecord,
} from '../../model';
import { useEstimationItemsTableRecords } from '../../useEstimationItemsTableRecords';
import DirectCostEstimationItemDetailModal from '../DirectCost/EstimationItemDetailModal';
import { directCostsMessageDefinitions } from '../DirectCost/messages';
import DirectCostSummaryTable from '../DirectCost/SummaryTable';
import { resourceCostsMessageDefinitions } from '../ResourceCost/messages';
import ResourceCostSummaryTable from '../ResourceCost/SummaryTable';
import { BasicProjectData } from './components/BasicProjectData';
import { useFilterState, useModalState } from './hooks/useManagementReportReducers';
import ManagementReportDirectCostTable from './ManagementReportDirectCostTable';
import ManagementReportResourceCostBreakdownSummaryTable, {
  buildAggregatedPhaseItems,
} from './ManagementReportResourceCostBreakdownSummaryTable';
import ManagementReportSummaryTable from './ManagementReportSummaryTable';
import messages from './messages';

const costItemsEmptyArray: Array<CostEstimationItemsTableRecord> = [];
const resourceItemsEmptyArray: Array<ResourceEstimationItemsTableRecord> = [];

// Constant ID for project description field in the filter
const PROJECT_DESCRIPTION_FIELD_ID = 'projectDescription';

const resourceCostExportOutputOptions = {
  showResourcePurposeEstimationItems: true,
  showEstimateColumn: true,
  showResourceCostColumns: true,
  showCostSumColumn: true,
  showAssumptionsColumn: true,
  showMasterdataColumn: true,
};

const directCostExportOutputOptions = {
  showCostPurposeEstimationItems: true,
  showDirectCostColumns: true,
  showCostSumColumn: true,
  showAssumptionsColumn: true,
  showMasterdataColumn: true,
};

const EstimationDetailManagementReportTab = () => {
  const { estimationId } = Route.useLoaderData();
  const resourceCostsMessages = useMessages(resourceCostsMessageDefinitions);
  const directCostsMessages = useMessages(directCostsMessageDefinitions);
  const sidebarMessages = useMessages(sidebarMessageDefinitions);
  const tabMessages = useMessages(estimationTabMessageDefinitions);
  const managementReportMessages = useMessages(messages);
  const projectInfoMessages = useMessages(ProjectInfoMessages);

  // --- Consolidated filter / visibility state ---
  const [filterState, filterDispatch] = useFilterState();
  const {
    searchText,
    hiddenCostTypeColumns,
    hiddenDirectCostSummaryColumns,
    hiddenFields,
    hiddenResourcePhases,
    hiddenCostPhases,
  } = filterState;

  // --- Consolidated modal stack state ---
  const [modalState, modalDispatch] = useModalState();
  const { resourceDetailItemIds, costDetailItemIds } = modalState;

  const {
    isLoading: isLoadingEstimationWithDependencies,
    data,
    getCostEstimationItemsTableRecordsByParent,
    getResourceEstimationItemsTableRecordsByParent,
  } = useEstimationItemsTableRecords(estimationId, {
    shouldFetchEstimationItemCosts: true,
    shouldFetchResources: true,
  });

  const { useGetCurrencies } = useCurrencyQueryActions();
  const { data: currencies, isLoading: isLoadingCurrencies } = useGetCurrencies() as {
    data: CurrencyResponse[] | undefined;
    isLoading: boolean;
  };

  const { useGetEstimationCosts } = useEstimationCostQueryActions();
  const { data: estimationCost, isLoading: isLoadingEstimationCost } = useGetEstimationCosts(
    estimationId,
  ) as { data: EstimationCostResponse | '' | undefined; isLoading: boolean };

  const { useGetEstimationAdditionalFields } = useEstimationQueryActions();
  const { data: additionalFields, isLoading: isLoadingAdditionalFields } =
    useGetEstimationAdditionalFields(estimationId) as {
      data: EstimationAdditionalFieldResponse[] | undefined;
      isLoading: boolean;
    };

  const topLevelResourceEstimationItems = useMemo(
    () => getResourceEstimationItemsTableRecordsByParent(undefined),
    [getResourceEstimationItemsTableRecordsByParent],
  );

  const topLevelCostEstimationItems = useMemo(
    () => getCostEstimationItemsTableRecordsByParent(undefined),
    [getCostEstimationItemsTableRecordsByParent],
  );

  const getIncludedResourceEstimationItemsTableRecordsByParent = useCallback(
    (parentId: string | undefined) =>
      getResourceEstimationItemsTableRecordsByParent(parentId).filter((item) => !item.isOmitted),
    [getResourceEstimationItemsTableRecordsByParent],
  );

  const getIncludedCostEstimationItemsTableRecordsByParent = useCallback(
    (parentId: string | undefined) =>
      getCostEstimationItemsTableRecordsByParent(parentId).filter((item) => !item.isOmitted),
    [getCostEstimationItemsTableRecordsByParent],
  );

  const allResourceEstimationItems = useMemo(() => {
    if (!data) return [];
    const collectAllResourceItems = (
      parentId: string | undefined,
    ): typeof topLevelResourceEstimationItems => {
      const items = getResourceEstimationItemsTableRecordsByParent(parentId);
      return items.flatMap((item) => [item, ...collectAllResourceItems(item.id)]);
    };
    return collectAllResourceItems(undefined);
  }, [data, getResourceEstimationItemsTableRecordsByParent]);

  const includedTopLevelResourceEstimationItems = useMemo(
    () => topLevelResourceEstimationItems.filter((item) => !item.isOmitted),
    [topLevelResourceEstimationItems],
  );

  const includedTopLevelCostEstimationItems = useMemo(
    () => topLevelCostEstimationItems.filter((item) => !item.isOmitted),
    [topLevelCostEstimationItems],
  );

  const includedAllResourceEstimationItems = useMemo(
    () => allResourceEstimationItems.filter((item) => !item.isOmitted),
    [allResourceEstimationItems],
  );

  const aggregatedResourceEstimationItemsForSummary = useMemo(
    () =>
      buildAggregatedPhaseItems(
        includedTopLevelResourceEstimationItems,
        includedAllResourceEstimationItems,
      ),
    [includedTopLevelResourceEstimationItems, includedAllResourceEstimationItems],
  );

  const filteredTopLevelResourceEstimationItems = useMemo(() => {
    if (!data || hiddenResourcePhases.length === 0) {
      return includedTopLevelResourceEstimationItems;
    }
    const hiddenPhaseAndDescendantIds = new Set<string>();
    hiddenResourcePhases.forEach((phaseId) => {
      const ids = getPhaseAndDescendantIds(phaseId, data.estimationItems);
      ids.forEach((id) => hiddenPhaseAndDescendantIds.add(id));
    });
    return includedTopLevelResourceEstimationItems.filter(
      (item) => !hiddenPhaseAndDescendantIds.has(item.id),
    );
  }, [includedTopLevelResourceEstimationItems, hiddenResourcePhases, data]);

  const filteredTopLevelCostEstimationItems = useMemo(() => {
    if (!data || hiddenCostPhases.length === 0) {
      return includedTopLevelCostEstimationItems;
    }
    const hiddenPhaseAndDescendantIds = new Set<string>();
    hiddenCostPhases.forEach((phaseId) => {
      const ids = getPhaseAndDescendantIds(phaseId, data.estimationItems);
      ids.forEach((id) => hiddenPhaseAndDescendantIds.add(id));
    });
    return includedTopLevelCostEstimationItems.filter(
      (item) => !hiddenPhaseAndDescendantIds.has(item.id),
    );
  }, [includedTopLevelCostEstimationItems, hiddenCostPhases, data]);

  const resourcePhasesForSelector = useMemo(() => {
    if (!data) return [];
    return data.estimationItems
      .filter(
        (item) =>
          !item.parentId &&
          item.purpose === ApiEstimationItemPurpose.RESOURCE &&
          item.status !== ApiEstimationItemStatus.OMITTED,
      )
      .map((item) => ({ id: item.id, name: item.name }));
  }, [data]);

  const costPhasesForSelector = useMemo(() => {
    if (!data) return [];
    return data.estimationItems
      .filter(
        (item) =>
          !item.parentId &&
          item.purpose === ApiEstimationItemPurpose.COST &&
          item.status !== ApiEstimationItemStatus.OMITTED,
      )
      .map((item) => ({ id: item.id, name: item.name }));
  }, [data]);

  const calculatedResultByEstimationItemId = useMemo(
    () =>
      new Map<string, number>(
        data?.estimationItems.map((item) => [item.id, item.calculatedResult ?? 0]),
      ),
    [data?.estimationItems],
  );

  const costModel = useMemo<CostModel | undefined>(() => {
    if (estimationCost === undefined || data?.estimationModel.currencyIds === undefined) {
      return undefined;
    }
    const allResourceTypes = sortResourceTypes(
      data.estimationModel.resourceTypes,
      data.estimationModel.resourceTypeGroups,
    );
    if (estimationCost === '') {
      return {
        currencyId: data.estimationModel.currencyIds[0],
        resourceTypeCosts: allResourceTypes.map((resourceType) => ({
          resourceTypeId: resourceType.id,
          costTypeId: resourceType.costTypeId,
          price: resourceType.resourceCost ?? 0,
        })),
      };
    }
    return {
      currencyId: estimationCost.currencyId,
      resourceTypeCosts: allResourceTypes.map((resourceType) => {
        const storedCost = estimationCost.resourceTypeCosts.find(
          (resourceTypeCost) => resourceTypeCost.resourceTypeId === resourceType.id,
        );
        return {
          resourceTypeId: resourceType.id,
          costTypeId: resourceType.costTypeId,
          price:
            storedCost === undefined || storedCost.updatedAt === null
              ? (resourceType.resourceCost ?? 0)
              : storedCost.price,
        };
      }),
    };
  }, [estimationCost, data]);

  const costTypeColumns: DataTableColumn<{ id: string }>[] = useMemo(() => {
    if (!data?.estimationModel) return [];
    const allCostTypes = sortCostTypes(
      data.estimationModel.costTypes,
      data.estimationModel.costTypeGroups,
    );
    return allCostTypes.map((costType) => ({
      accessor: costType.id,
      title: costType.name,
    }));
  }, [data]);

  const fieldColumns: DataTableColumn<{ id: string }>[] = useMemo(() => {
    const columns: DataTableColumn<{ id: string }>[] = [];
    const projectDescription = data?.estimation.projectDescription;
    if (projectDescription && projectDescription.trim() !== '') {
      columns.push({
        accessor: PROJECT_DESCRIPTION_FIELD_ID,
        title: projectInfoMessages.projectDescriptionLabel(),
      });
    }
    if (data?.estimationModel.additionalFields && additionalFields) {
      const fieldsData = data.estimationModel.additionalFields
        .sort(sortExpressions.byOrder)
        .map((fieldDefinition) => {
          const fieldValue = additionalFields.find(
            (field) => field.estimationModelAdditionalFieldDefinitionId === fieldDefinition.id,
          );
          return {
            definition: fieldDefinition,
            value: fieldValue?.value || null,
          };
        });
      const fieldsWithValues = fieldsData.filter(
        (item) => item.value !== null && item.value !== '',
      );
      const additionalFieldColumns = fieldsWithValues.map((field) => ({
        accessor: field.definition.id,
        title: field.definition.name,
      }));
      columns.push(...additionalFieldColumns);
    }
    return columns;
  }, [data, additionalFields, projectInfoMessages]);

  const handleFieldsChange = useCallback(
    (visibleFields: DataTableColumn<{ id: string }>[]) => {
      const visibleAccessors = new Set(visibleFields.map((field) => field.accessor as string));
      const allAccessors = fieldColumns.map((field) => field.accessor as string);
      const hidden = allAccessors.filter((accessor) => !visibleAccessors.has(accessor));
      filterDispatch({ type: 'SET_HIDDEN_FIELDS', payload: hidden });
    },
    [fieldColumns, filterDispatch],
  );

  const handleColumnsChange = useCallback(
    (visibleColumns: DataTableColumn<{ id: string }>[]) => {
      const visibleAccessors = new Set(visibleColumns.map((col) => col.accessor as string));
      const allAccessors = costTypeColumns.map((col) => col.accessor as string);
      const hidden = allAccessors.filter((accessor) => !visibleAccessors.has(accessor));
      filterDispatch({ type: 'SET_HIDDEN_COST_TYPE_COLUMNS', payload: hidden });
    },
    [costTypeColumns, filterDispatch],
  );

  const handleDirectCostSummaryColumnsChange = useCallback(
    (visibleColumns: DataTableColumn<{ id: string }>[]) => {
      const visibleAccessors = new Set(visibleColumns.map((col) => col.accessor as string));
      const allAccessors = costTypeColumns.map((col) => col.accessor as string);
      const hidden = allAccessors.filter((accessor) => !visibleAccessors.has(accessor));
      filterDispatch({ type: 'SET_HIDDEN_DIRECT_COST_SUMMARY_COLUMNS', payload: hidden });
    },
    [costTypeColumns, filterDispatch],
  );

  const currencyCode = useMemo(() => {
    if (!currencies || !estimationCost) return undefined;
    return currencies.find((x) => x.id === estimationCost.currencyId)?.code;
  }, [currencies, estimationCost]);

  const combinedResourceAndCostItemsExportProps = useMemo<
    OmitSafely<EstimationExportButtonsProps, 'parentEstimationItemId'>
  >(
    () => ({
      estimationId,
      costModel,
      title: `${sidebarMessages.detailedEstimation()} - Management Report`,
      outputOptionsArray: [
        {
          ...resourceCostExportOutputOptions,
          tableTitle: managementReportMessages.resourceCostBreakdownSummary(),
          showOnlyPhases: true,
        },
        {
          ...directCostExportOutputOptions,
          tableTitle: directCostsMessages.costBreakDownTitle(),
        },
        {
          ...resourceCostExportOutputOptions,
          tableTitle: managementReportMessages.resourceCostBreakdownDetail(),
        },
      ],
      hiddenCostTypeColumns,
      hiddenDirectCostSummaryColumns,
      hiddenFields,
      hiddenPhases: [...hiddenResourcePhases, ...hiddenCostPhases],
    }),
    [
      estimationId,
      costModel,
      managementReportMessages,
      directCostsMessages,
      sidebarMessages,
      hiddenCostTypeColumns,
      hiddenDirectCostSummaryColumns,
      hiddenFields,
      hiddenResourcePhases,
      hiddenCostPhases,
    ],
  );

  const combinedExporter = useEstimationExport(combinedResourceAndCostItemsExportProps);
  const handleExportButtonClick = useCallback(
    (format: eExportFormat) => combinedExporter(format, undefined),
    [combinedExporter],
  );

  const exportConfiguration = useMemo<eExportFormat[]>(
    () => [
      eExportFormat.EXCEL,
      eExportFormat.PDF,
      eExportFormat.CSV,
      eExportFormat.JSON,
      eExportFormat.PRINT,
    ],
    [],
  );

  // --- Early returns ---

  if (
    isLoadingEstimationWithDependencies ||
    isLoadingEstimationCost ||
    isLoadingCurrencies ||
    isLoadingAdditionalFields
  ) {
    return (
      <Layout>
        <h2 className='mt-5'>{tabMessages.managementReportLabel()}</h2>
        <TableSkeleton />
        <TableSkeleton />
      </Layout>
    );
  }

  if (data === undefined || currencies === undefined) {
    return (
      <Layout>
        <TableSkeleton />
      </Layout>
    );
  }

  if (!data.estimationItemCosts) {
    throw new Error('Estimation item costs cannot be undefined.');
  }

  if (!estimationCost) {
    return <Layout>Estimation cost not defined yet</Layout>;
  }

  // --- Callbacks that depend on post-guard data ---

  // The grouped tables require onAssumptionsClicked / onCommentsClicked, but the
  // management report is a read-only view that never opens those modals, so both
  // are intentional no-ops (matching the prior, never-read state behaviour).
  const noopItemAction: EstimationItemActionCallback = () => {};
  const onAssumptionsClicked = noopItemAction;
  const onCommentsClicked = noopItemAction;

  const onResourceItemDetailsClicked: EstimationItemActionCallback = (estimationItemId) =>
    modalDispatch({ type: 'PUSH_RESOURCE_DETAIL_ITEM', payload: estimationItemId });

  const onCostItemDetailsClicked: EstimationItemActionCallback = (estimationItemId) =>
    modalDispatch({ type: 'PUSH_COST_DETAIL_ITEM', payload: estimationItemId });

  const resourceTableProps: OmitSafely<
    EstimationItemsGroupedTableProps,
    'estimationItems' | 'control' | 'searchText' | 'formModelEstimationItems'
  > = {
    estimationModel: data.estimationModel,
    methodologies: data.methodologies,
    costs: costModel,
    showButtons: true,
    showMethodology: false,
    showResponsible: false,
    showParameters: false,
    showCosts: true,
    isReadOnly: true,
    onIncludeClicked: () => Promise.reject(new Error('Read-only')),
    estimationUnit: data.estimation.unit,
    onAssumptionsClicked,
    onCommentsClicked,
    onDetailsClicked: onResourceItemDetailsClicked,
    calculatedResultByEstimationItemId,
  };

  const costTableProps: OmitSafely<
    CostEstimationItemsGroupedTableProps,
    'estimationItems' | 'control' | 'onIncludeClicked' | 'searchText'
  > = {
    estimationModel: data.estimationModel,
    currencyCode: currencyCode,
    showButtons: true,
    isReadOnly: true,
    onAssumptionsClicked,
    onCommentsClicked,
    onDetailsClicked: onCostItemDetailsClicked,
  };

  return (
    <Layout>
      <Stack gap={10}>
        {/* Toolbar: field visibility + export */}
        <div className='mt-4 flex justify-end gap-2 rounded-xl'>
          {fieldColumns.length > 0 && (
            <TableColumnsSelector
              columns={fieldColumns}
              onColumnsChange={handleFieldsChange}
              hiddenColumns={hiddenFields}
              label={managementReportMessages.fields()}
            />
          )}
          <ExportButtonsSelector
            exportConfiguration={exportConfiguration}
            onExportButtonClick={handleExportButtonClick}
          />
        </div>
        <BasicProjectData
          projectDescription={data.estimation.projectDescription}
          additionalFieldDefinitions={data.estimationModel.additionalFields}
          additionalFields={additionalFields}
          hiddenFields={hiddenFields}
        />
        {/* Table 2: Resource Cost Breakdown Summary */}
        <div className='mt-4'>
          <h2>{managementReportMessages.resourceCostBreakdownSummary()}</h2>
        </div>
        <p className='font-extralightm text-sm'>
          {resourceCostsMessages.estimationUnit()}:{' '}
          <EstimationUnitName estimationUnit={data.estimation.unit} />
        </p>
        <p className='font-extralightm text-sm'>
          {resourceCostsMessages.costUnit()} :{' '}
          <CurrencyCode currencyId={estimationCost.currencyId} />
        </p>
        <ManagementReportResourceCostBreakdownSummaryTable
          resourceEstimationItems={includedTopLevelResourceEstimationItems}
          allResourceEstimationItems={includedAllResourceEstimationItems}
          estimationModel={data.estimationModel}
          costs={costModel}
          assumptions={data.assumptions}
        />
        <ResourceCostSummaryTable
          estimationItems={aggregatedResourceEstimationItemsForSummary}
          estimationModel={data.estimationModel}
          costs={costModel}
          showCosts
          isManagementReportTab={true}
          hiddenCostTypeColumns={[]}
        />
        <hr />

        {/* Table 3: Direct Cost Breakdown */}
        <div className='mt-4 flex items-center justify-between gap-2 rounded-xl'>
          <h3>{directCostsMessages.costBreakDownTitle()}</h3>
          <div className='flex gap-2'>
            {costPhasesForSelector.length > 0 && (
              <TablePhasesSelector
                phases={costPhasesForSelector}
                hiddenPhases={hiddenCostPhases}
                onPhasesChange={(phases) =>
                  filterDispatch({ type: 'SET_HIDDEN_COST_PHASES', payload: phases })
                }
              />
            )}
            {costTypeColumns.length > 0 && (
              <TableColumnsSelector
                columns={costTypeColumns}
                onColumnsChange={handleDirectCostSummaryColumnsChange}
                hiddenColumns={hiddenDirectCostSummaryColumns}
              />
            )}
          </div>
        </div>
        <p className='font-extralightm text-sm'>
          {resourceCostsMessages.costUnit()} :{' '}
          <CurrencyCode currencyId={estimationCost.currencyId} />
        </p>
        <ManagementReportDirectCostTable
          resourceEstimationItems={filteredTopLevelResourceEstimationItems}
          costEstimationItems={filteredTopLevelCostEstimationItems}
          estimationModel={data.estimationModel}
          costs={costModel}
          getResourceEstimationItemsTableRecordsByParent={
            getIncludedResourceEstimationItemsTableRecordsByParent
          }
          getCostEstimationItemsTableRecordsByParent={
            getIncludedCostEstimationItemsTableRecordsByParent
          }
          assumptions={data.assumptions}
          searchText={searchText}
          hiddenCostTypeColumns={hiddenDirectCostSummaryColumns}
        />
        <DirectCostSummaryTable
          estimationItems={filteredTopLevelCostEstimationItems}
          estimationModel={data.estimationModel}
          costs={costModel}
          showAssumptions={true}
          hiddenCostTypeColumns={hiddenDirectCostSummaryColumns}
        />
        <hr />

        {/* Table 4: Resource Cost Breakdown Detail — with filters */}
        <div className='mt-4 flex items-center justify-between gap-2 rounded-xl'>
          <h2>{managementReportMessages.resourceCostBreakdownDetail()}</h2>
          <div className='flex gap-2'>
            <Search
              handleOnChange={(text) => filterDispatch({ type: 'SET_SEARCH_TEXT', payload: text })}
            />
            {resourcePhasesForSelector.length > 0 && (
              <TablePhasesSelector
                phases={resourcePhasesForSelector}
                hiddenPhases={hiddenResourcePhases}
                onPhasesChange={(phases) =>
                  filterDispatch({ type: 'SET_HIDDEN_RESOURCE_PHASES', payload: phases })
                }
              />
            )}
            {costTypeColumns.length > 0 && (
              <TableColumnsSelector
                columns={costTypeColumns}
                onColumnsChange={handleColumnsChange}
                hiddenColumns={hiddenCostTypeColumns}
              />
            )}
          </div>
        </div>
        <p className='font-extralightm text-sm'>
          {resourceCostsMessages.estimationUnit()}:{' '}
          <EstimationUnitName estimationUnit={data.estimation.unit} />
        </p>
        <p className='font-extralightm text-sm'>
          {resourceCostsMessages.costUnit()} :{' '}
          <CurrencyCode currencyId={estimationCost.currencyId} />
        </p>
        <ManagementReportSummaryTable
          resourceEstimationItems={filteredTopLevelResourceEstimationItems}
          costEstimationItems={filteredTopLevelCostEstimationItems}
          estimationModel={data.estimationModel}
          costs={costModel}
          getResourceEstimationItemsTableRecordsByParent={
            getIncludedResourceEstimationItemsTableRecordsByParent
          }
          getCostEstimationItemsTableRecordsByParent={
            getIncludedCostEstimationItemsTableRecordsByParent
          }
          assumptions={data.assumptions}
          searchText={searchText}
          hiddenCostTypeColumns={hiddenCostTypeColumns}
        />
        <ResourceCostSummaryTable
          estimationItems={filteredTopLevelResourceEstimationItems}
          estimationModel={data.estimationModel}
          costs={costModel}
          showCosts
          isManagementReportTab={true}
          hiddenCostTypeColumns={hiddenCostTypeColumns}
        />
      </Stack>

      {/* Resource item detail modals — stacked by push/pop */}
      {resourceDetailItemIds.map((resourceEstimationItemIdForDetail) => (
        <EstimationItemDetailModal
          key={resourceEstimationItemIdForDetail}
          open={!!resourceEstimationItemIdForDetail}
          onClose={() => modalDispatch({ type: 'POP_RESOURCE_DETAIL_ITEM' })}
          onSubmit={() => Promise.resolve()}
          shouldResetForm={true}
          onFormReset={() => {}}
          estimationItem={
            resourceEstimationItemIdForDetail === undefined
              ? undefined
              : getResourceEstimationItemDetail(resourceEstimationItemIdForDetail, data)
          }
          estimationItems={
            resourceEstimationItemIdForDetail
              ? getResourceEstimationItemsTableRecordsByParent(resourceEstimationItemIdForDetail)
              : resourceItemsEmptyArray
          }
          estimationMethodologyId={data.estimation.methodologyId}
          estimationCultureName={data.estimation.cultureName}
          exportProps={combinedResourceAndCostItemsExportProps}
          {...resourceTableProps}
        />
      ))}

      {/* Cost item detail modals — stacked by push/pop */}
      {costDetailItemIds.map((costEstimationItemIdForDetail) => (
        <DirectCostEstimationItemDetailModal
          key={costEstimationItemIdForDetail}
          open={!!costEstimationItemIdForDetail}
          onClose={() => modalDispatch({ type: 'POP_COST_DETAIL_ITEM' })}
          onSubmit={() => Promise.resolve()}
          estimationItem={
            costEstimationItemIdForDetail === undefined
              ? undefined
              : getCostEstimationItemDetail(costEstimationItemIdForDetail, data)
          }
          estimationItems={
            costEstimationItemIdForDetail
              ? getCostEstimationItemsTableRecordsByParent(costEstimationItemIdForDetail)
              : costItemsEmptyArray
          }
          exportProps={combinedResourceAndCostItemsExportProps}
          keepDirtyValues={false}
          {...costTableProps}
        />
      ))}
    </Layout>
  );
};

export default EstimationDetailManagementReportTab;
